import React, { useState, useEffect } from 'react';
import {
    AppBar as MuiAppBar, Toolbar, IconButton, Menu, MenuItem, Typography, Box,
    Avatar, Divider, ListItemIcon, alpha, Tooltip, Button, Chip,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import AddIcon from '@mui/icons-material/Add';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SearchIcon from '@mui/icons-material/Search';
import Logo from '../../assets/Alerta Normativa-logo2026-claro.png';
import LogoBlanco from '../../assets/Alerta Normativa-logo2026-blanco.png';
import { jwtDecode } from 'jwt-decode';
import { cerrarSesion } from '../http/HttpCliente';
import NotificacionesMenu from './NotificacionMenu';
import NotificacionBandejaMenu from './NotificacionBandejaMenu';
import ThemeToggle from './ThemeToggle';
import { useColorMode } from '../../styles/ColorModeProvider';
import { brandGradient } from '../../styles/theme';
import { APPBAR_HEIGHT } from '../../styles/layout';
import CommandPalette from './CommandPalette';

const ATAJO = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform) ? '⌘ K' : 'Ctrl K';

const AppBar = ({ handleDrawerToggle, isOpen, toggleDrawer }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [userName, setUserName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [isAdmin, setIsAdmin] = useState(false);
    const [rol, setRol] = useState('');
    const [buscadorAbierto, setBuscadorAbierto] = useState(false);
    const navigate = useNavigate();
    const { mode } = useColorMode();

    useEffect(() => {
        const onKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setBuscadorAbierto((v) => !v);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserName(decoded["given_name"] || '');
                setUserLastName(decoded["family_name"] || '');
                setRol(decoded["rol"] || '');
                setIsAdmin(decoded["rol"] === "Administrador");
            } catch (error) {
                console.error('Error decoding token:', error);
                handleLogout();
            }
        } else {
            navigate('/login');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navigate]);

    const isSuscriptor = rol === "Suscriptor";

    const rolLabel = isAdmin
        ? 'Administrador'
        : isSuscriptor
            ? 'Suscriptor'
            : 'Usuario';

    // El logo del suscriptor no debe llevar a /home (no es su vista);
    // lo mandamos a su bandeja.
    const homeHref = isSuscriptor ? "/suscriptor/bandeja" : "/home";

    const handleMenu = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleLogout = async () => {
        await cerrarSesion();
        navigate('/login');
    };

    const handlePerfilUsuario = () => {
        handleClose();
        navigate('/perfil-usuario');
    };

    const initials = `${userName[0] || ''}${userLastName[0] || ''}`.toUpperCase();

    const avatarSx = {
        background: brandGradient,
        color: '#fff',
        fontWeight: 700,
    };

    return (
        <MuiAppBar
            position="fixed"
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0, left: 0, right: 0,
                    height: 3,
                    background: brandGradient,
                },
            }}
        >
            <Toolbar sx={{ justifyContent: 'space-between', minHeight: APPBAR_HEIGHT, gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <IconButton
                        aria-label="abrir menú"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ display: { sm: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Tooltip title={isOpen ? 'Contraer menú' : 'Expandir menú'}>
                        <IconButton
                            aria-label="contraer menú"
                            edge="start"
                            onClick={toggleDrawer}
                            sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.secondary' }}
                        >
                            {isOpen ? <MenuOpenIcon /> : <MenuIcon />}
                        </IconButton>
                    </Tooltip>
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', ml: 0.5 }}>
                        <Link style={{ textDecoration: 'none', color: 'inherit', display: 'flex' }} to={homeHref}>
                            <Box
                                component="img"
                                src={mode === 'dark' ? LogoBlanco : Logo}
                                alt="Alerta Normativa Global"
                                sx={{ height: 42, display: 'block' }}
                            />
                        </Link>
                    </Box>
                    {isAdmin && (
                        <>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ mx: 1.5, my: 2.25, display: { xs: 'none', md: 'block' } }}
                            />
                            <Button
                                component={Link}
                                to="/nueva-normativa"
                                variant="contained"
                                startIcon={<AddIcon />}
                                sx={{ display: { xs: 'none', md: 'inline-flex' }, borderRadius: 999, px: 2 }}
                            >
                                Nueva normativa
                            </Button>
                            <Tooltip title="Nueva normativa">
                                <IconButton
                                    component={Link}
                                    to="/nueva-normativa"
                                    aria-label="Nueva normativa"
                                    sx={{
                                        display: { xs: 'inline-flex', md: 'none' },
                                        ml: 0.5,
                                        bgcolor: 'primary.main',
                                        color: '#fff',
                                        '&:hover': { bgcolor: 'primary.dark' },
                                    }}
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box
                        component="button"
                        type="button"
                        onClick={() => setBuscadorAbierto(true)}
                        aria-label="Abrir buscador"
                        sx={(t) => ({
                            display: { xs: 'none', md: 'flex' },
                            alignItems: 'center',
                            gap: 1,
                            width: { md: 220, lg: 280 },
                            mr: 1,
                            px: 1.5,
                            height: 38,
                            borderRadius: 999,
                            cursor: 'pointer',
                            font: 'inherit',
                            fontSize: '0.85rem',
                            color: 'text.secondary',
                            bgcolor: t.palette.mode === 'light' ? '#F3F6F4' : alpha('#fff', 0.05),
                            border: `1px solid ${t.palette.divider}`,
                            transition: 'border-color .15s, background-color .15s',
                            '&:hover': { borderColor: alpha(t.palette.primary.main, 0.4) },
                            '&:focus-visible': { outline: `2px solid ${alpha(t.palette.primary.main, 0.5)}`, outlineOffset: 2 },
                        })}
                    >
                        <SearchIcon fontSize="small" />
                        <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>Buscar…</Box>
                        <kbd>{ATAJO}</kbd>
                    </Box>
                    <Tooltip title={`Buscar (${ATAJO})`}>
                        <IconButton
                            onClick={() => setBuscadorAbierto(true)}
                            aria-label="Abrir buscador"
                            sx={{ display: { xs: 'inline-flex', md: 'none' }, color: 'text.secondary' }}
                        >
                            <SearchIcon />
                        </IconButton>
                    </Tooltip>
                    <ThemeToggle sx={{ color: 'text.secondary' }} />
                    {isAdmin && <NotificacionBandejaMenu />}
                    {/* Las notificaciones internas no aplican al suscriptor */}
                    {!isSuscriptor && <NotificacionesMenu />}

                    <Divider orientation="vertical" flexItem sx={{ mx: 1, my: 2.25, display: { xs: 'none', sm: 'block' } }} />

                    <Box
                        onClick={handleMenu}
                        role="button"
                        aria-haspopup="true"
                        aria-controls="menu-appbar"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            pl: 0.5,
                            pr: { xs: 0.5, sm: 1 },
                            py: 0.5,
                            borderRadius: 999,
                            cursor: 'pointer',
                            border: '1px solid transparent',
                            transition: 'background-color .15s, border-color .15s',
                            '&:hover': {
                                backgroundColor: (t) => alpha(t.palette.primary.main, 0.06),
                                borderColor: (t) => alpha(t.palette.primary.main, 0.15),
                            },
                        }}
                    >
                        <Avatar sx={{ ...avatarSx, width: 36, height: 36, fontSize: '0.82rem' }}>
                            {initials}
                        </Avatar>
                        <Box sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.15, maxWidth: 160 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                                {userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                {rolLabel}
                            </Typography>
                        </Box>
                        <KeyboardArrowDownIcon
                            fontSize="small"
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                color: 'text.secondary',
                                transition: 'transform .2s',
                                transform: anchorEl ? 'rotate(180deg)' : 'none',
                            }}
                        />
                    </Box>

                    <Menu
                        id="menu-appbar"
                        anchorEl={anchorEl}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        slotProps={{
                            paper: { sx: { mt: 1, minWidth: 250 } },
                        }}
                    >
                        <Box sx={{ px: 1.5, pt: 1, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ ...avatarSx, width: 42, height: 42, fontSize: '0.95rem' }}>
                                {initials}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" noWrap>{userName} {userLastName}</Typography>
                                <Chip
                                    label={rolLabel}
                                    size="small"
                                    color="primary"
                                    sx={{ mt: 0.5, height: 20, fontSize: '0.68rem' }}
                                />
                            </Box>
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        {/* El suscriptor no tiene vista de perfil interno */}
                        {!isSuscriptor && (
                            <MenuItem onClick={handlePerfilUsuario}>
                                <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
                                Perfil de usuario
                            </MenuItem>
                        )}
                        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                            <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
                            Cerrar sesión
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
            <CommandPalette open={buscadorAbierto} onClose={() => setBuscadorAbierto(false)} />
        </MuiAppBar>
    );
};

export default AppBar;
